// N8N-IMPORT: aba PROVISÓRIA de migração do n8n — remover este arquivo (e a
// entrada em $agentId.tsx, o router n8n_import.py e os fallbacks marcados com
// N8N-IMPORT no worker/API) quando as migrações terminarem.
import { useState, type CSSProperties } from "react";
import { api } from "../../lib/api";
import { toastError } from "../shared/toast";

interface Preview {
  found: boolean;
  messages: number;
  clients: number;
  blocks: number;
}

interface RunResult {
  conversations_imported: number;
  conversations_skipped: number;
  messages_imported: number;
  blocks_imported: number;
  blocks_skipped: number;
}

const cardStyle: CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--line-strong)",
  borderRadius: 12,
  padding: 20,
  maxWidth: 560,
};

export function TabImportN8n({ agent }: { agent: any }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<string[] | null>(null);
  const [agenteid, setAgenteid] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [running, setRunning] = useState(false);

  // Busca parcial/case-insensitive pelos agentids do n8n (parte antes do ":").
  const search = async () => {
    const q = query.trim();
    if (q.length < 2 || searching) return;
    setSearching(true);
    setMatches(null);
    setAgenteid(null);
    setPreview(null);
    setResult(null);
    try {
      setMatches(
        await api.get<string[]>(
          `/agents/${agent.id}/n8n-import/search?q=${encodeURIComponent(q)}`,
        ),
      );
    } catch (e) {
      toastError((e as any)?.payload?.detail ?? "Falha ao buscar no n8n.");
    } finally {
      setSearching(false);
    }
  };

  const select = async (id: string) => {
    if (loadingPreview) return;
    setAgenteid(id);
    setPreview(null);
    setResult(null);
    setLoadingPreview(true);
    try {
      setPreview(
        await api.get<Preview>(
          `/agents/${agent.id}/n8n-import/preview?agenteid=${encodeURIComponent(id)}`,
          120_000, // SCAN no keyspace do n8n pode levar bem mais que os 15s default
        ),
      );
    } catch (e) {
      toastError((e as any)?.payload?.detail ?? "Falha ao carregar o resumo.");
      setAgenteid(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const run = async () => {
    if (running || !agenteid) return;
    setRunning(true);
    try {
      // Migração síncrona — timeout largo pra volumes maiores.
      setResult(
        await api.post<RunResult>(
          `/agents/${agent.id}/n8n-import/run`,
          { agenteid },
          300_000,
        ),
      );
    } catch (e) {
      toastError((e as any)?.payload?.detail ?? "Falha na migração.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={cardStyle}>
        <h3 style={{ margin: 0, fontSize: 15, color: "var(--ink)" }}>Importar do n8n</h3>
        <p style={{ margin: "6px 0 14px", fontSize: 13, color: "var(--ink-muted)" }}>
          Migra os últimos 30 dias de conversas e todas as chaves de bloqueio humano
          (<code>_block</code>) do agenteid informado para <b>este</b> agente. Telefones
          já ativos neste agente não são tocados. Ferramenta provisória de migração.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="buscar agenteid no n8n (ex: mago)"
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid var(--line-strong)",
              background: "var(--surface)",
              color: "var(--ink)",
              fontSize: 13,
            }}
          />
          <button
            onClick={search}
            disabled={searching || query.trim().length < 2}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--line-strong)",
              background: "var(--surface-hover)",
              color: "var(--ink)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {searching ? "Buscando…" : "Buscar"}
          </button>
        </div>
      </div>

      {matches && matches.length === 0 && (
        <div style={cardStyle}>
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>
            Nenhum agenteid parecido encontrado nos últimos 30 dias do n8n.
          </span>
        </div>
      )}

      {matches && matches.length > 0 && (
        <div style={cardStyle}>
          <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "var(--ink)" }}>
            Agentes encontrados — selecione um
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {matches.map((id) => (
              <button
                key={id}
                onClick={() => select(id)}
                disabled={loadingPreview}
                style={{
                  padding: "7px 14px",
                  borderRadius: 8,
                  border: `1px solid ${id === agenteid ? "var(--ok-line)" : "var(--line-strong)"}`,
                  background: id === agenteid ? "var(--ok-soft)" : "var(--surface-hover)",
                  color: id === agenteid ? "var(--ok)" : "var(--ink)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {id}
              </button>
            ))}
          </div>
          {loadingPreview && (
            <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--ink-muted)" }}>
              Carregando resumo…
            </p>
          )}
        </div>
      )}

      {preview && !preview.found && (
        <div style={cardStyle}>
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>
            Nada a migrar para <b>{agenteid}</b> (sem conversas nos últimos 30 dias e sem
            chaves de block).
          </span>
        </div>
      )}

      {preview?.found && (
        <div style={cardStyle}>
          <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "var(--ink)" }}>
            Encontrado no n8n — <b>{agenteid}</b>
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.9 }}>
            <li><b>{preview.messages.toLocaleString("pt-BR")}</b> mensagens nos últimos 30 dias</li>
            <li><b>{preview.clients.toLocaleString("pt-BR")}</b> clientes únicos</li>
            <li><b>{preview.blocks.toLocaleString("pt-BR")}</b> bloqueios humanos ativos (chaves _block)</li>
          </ul>
          {!result && (
            <button
              onClick={run}
              disabled={running}
              style={{
                marginTop: 14,
                padding: "9px 18px",
                borderRadius: 8,
                border: "1px solid var(--ok-line)",
                background: "var(--ok-soft)",
                color: "var(--ok)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {running ? "Migrando… (pode levar alguns minutos)" : "Iniciar migração"}
            </button>
          )}
        </div>
      )}

      {result && (
        <div style={{ ...cardStyle, border: "1px solid var(--ok-line)" }}>
          <h4 style={{ margin: "0 0 10px", fontSize: 14, color: "var(--ok)" }}>
            Migração concluída
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.9 }}>
            <li><b>{result.conversations_imported.toLocaleString("pt-BR")}</b> conversas importadas ({result.messages_imported.toLocaleString("pt-BR")} mensagens)</li>
            <li><b>{result.conversations_skipped.toLocaleString("pt-BR")}</b> conversas puladas (telefone já existia neste agente)</li>
            <li><b>{result.blocks_imported.toLocaleString("pt-BR")}</b> bloqueios importados, <b>{result.blocks_skipped.toLocaleString("pt-BR")}</b> pulados</li>
          </ul>
        </div>
      )}
    </div>
  );
}
